import { Accordion } from '@mui/material';
import { useState, SyntheticEvent, Fragment } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { isEmpty, isEqual } from 'lodash';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import { ProductsModel } from '../../../interface/products';
import {
  inchDepth,
  inchHeight,
  inchWidth,
  millimeterDepth,
  millimeterHeight,
  millimeterWidth
} from '../../../constants/products';
import {
  checkIfProductIsDomestic,
  inchToMillimeter,
  millimeterToInch
} from '../../../utils/products';
import * as S from './styles';
import SpecificationText from '../../atoms/specification-text/specification-text';

/* eslint-disable-next-line */
type ProductAccordionProps = {
  productDetails: ProductsModel;
};

export function ProductAccordion(props: ProductAccordionProps) {
  const { productDetails } = props;
  const { attributes } = productDetails;
  const { Specifications } = attributes;
  const [expanded, setExpanded] = useState<string | false>('Product Overview');

  const handleChange =
    (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const { dimensionsUnit, isDomesticProduct, lengthUnit } =
    checkIfProductIsDomestic(attributes.ModelNumber);

  const productDimensions = isDomesticProduct ? (
    <>
      <SpecificationText
        text={`Dimensions (in): ${Specifications?.OverallWidth} ${inchWidth} x ${Specifications?.OverallHeight} ${inchHeight} x ${Specifications?.OverallDepth} ${inchDepth}`}
      />
      <SpecificationText
        text={`Dimensions (mm): ${inchToMillimeter(
          Specifications?.OverallWidth
        )} ${millimeterWidth} x ${inchToMillimeter(
          Specifications?.OverallHeight
        )} ${millimeterHeight} x ${inchToMillimeter(
          Specifications?.OverallDepth
        )} ${millimeterDepth}`}
      />
    </>
  ) : (
    <>
      <SpecificationText
        text={`Dimensions (in): ${millimeterToInch(
          Specifications?.OverallWidth
        )} ${inchWidth} x ${millimeterToInch(
          Specifications?.OverallHeight
        )} ${inchHeight} x ${millimeterToInch(
          Specifications?.OverallDepth
        )} ${inchDepth}`}
      />
      <SpecificationText
        text={`Dimensions (mm): ${Specifications?.OverallWidth} ${millimeterWidth} x
    ${Specifications?.OverallHeight} ${millimeterHeight} x
    ${Specifications?.OverallDepth} ${millimeterDepth}`}
      />
    </>
  );

  const renderSpecifications = (
    <div>
      {Specifications?.OverallWidth ? productDimensions : <Fragment />}
      {Specifications.Weight ? (
        <SpecificationText text={`Weight: ${Specifications.Weight} kg`} />
      ) : (
        <Fragment />
      )}
      {Specifications.ElectricalSupply ? (
        <SpecificationText
          text={`Electrical Supply: ${Specifications.ElectricalSupply}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.ElectricalService ? (
        <SpecificationText
          text={`Electrical Service: ${Specifications.ElectricalService}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.GasSupply ? (
        <SpecificationText text={`Gas Supply: ${Specifications.GasSupply}`} />
      ) : (
        <Fragment />
      )}
      {Specifications.GasInlet ? (
        <SpecificationText text={`Gas Inlet: ${Specifications.GasInlet}`} />
      ) : (
        <Fragment />
      )}
      {Specifications.PowerCordLength ? (
        <SpecificationText
          text={`Power Cord: ${Specifications.PowerCordLength} ${lengthUnit}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.Capacity ? (
        <SpecificationText text={`Capacity: ${Specifications.Capacity}`} />
      ) : (
        <Fragment />
      )}
      {Specifications.FreezerCapacity ? (
        <SpecificationText
          text={`Freezer Capacity: ${Specifications.FreezerCapacity}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.RefrigeratorCapacity ? (
        <SpecificationText
          text={`Refrigerator Capacity: ${Specifications.RefrigeratorCapacity}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.WineStorageCapacity ? (
        <SpecificationText
          text={`Wine Storage Capacity: ${Specifications.WineStorageCapacity}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.UsableCapacity ? (
        <SpecificationText
          text={`Usable Capacity: ${Specifications.UsableCapacity}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.DoorClearance ? (
        <SpecificationText
          text={`Door Clearance: ${Specifications.DoorClearance} ${dimensionsUnit}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.DrawerClearance ? (
        <SpecificationText
          text={`Drawer Clearance: ${Specifications.DrawerClearance} ${dimensionsUnit}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.DischargeLocation ? (
        <SpecificationText
          text={`Discharge Location: ${Specifications.DischargeLocation}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.BottomOfHoodToCountertop ? (
        <SpecificationText
          text={`Bottom of hood to countertop: ${Specifications.BottomOfHoodToCountertop}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.InteriorDepth ? (
        <SpecificationText
          text={`Interior Depth: ${Specifications.InteriorDepth} ${dimensionsUnit}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.InteriorHeight ? (
        <SpecificationText
          text={`Interior Height: ${Specifications.InteriorHeight} ${dimensionsUnit}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.InteriorWidth ? (
        <SpecificationText
          text={`Interior Width: ${Specifications.InteriorWidth} ${dimensionsUnit}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.Oven1InteriorDepth ? (
        <SpecificationText
          text={`Oven 1 Interior Depth: ${Specifications.Oven1InteriorDepth}
        ${dimensionsUnit}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.Oven1InteriorHeight ? (
        <SpecificationText
          text={`Oven 1 Interior Height: ${Specifications.Oven1InteriorHeight}
        ${dimensionsUnit}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.Oven1InteriorWidth ? (
        <SpecificationText
          text={`Oven 1 Interior Width: ${Specifications.Oven1InteriorWidth}
        ${dimensionsUnit}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.Oven1OverallCapacity ? (
        <SpecificationText
          text={`Oven 1 Overall Capacity: ${Specifications.Oven1OverallCapacity}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.Oven1UsableCapacity ? (
        <SpecificationText
          text={`Oven 1 Usable Capacity: ${Specifications.Oven1UsableCapacity}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.Oven2InteriorDepth ? (
        <SpecificationText
          text={`Oven 2 Interior Depth: ${Specifications.Oven2InteriorDepth}
        ${dimensionsUnit}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.Oven2InteriorHeight ? (
        <SpecificationText
          text={`Oven 2 Interior Height: ${Specifications.Oven2InteriorHeight}
        ${dimensionsUnit}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.Oven2InteriorWidth ? (
        <SpecificationText
          text={` Oven 2 Interior Width: ${Specifications.Oven2InteriorWidth}
        ${dimensionsUnit}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.Oven2OverallCapacity ? (
        <SpecificationText
          text={`Oven 2 Overall Capacity: ${Specifications.Oven2OverallCapacity}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.Oven2UsableCapacity ? (
        <SpecificationText
          text={`Oven 2 Usable Capacity: ${Specifications.Oven2UsableCapacity}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.OverallCapacity ? (
        <SpecificationText
          text={`Overall Capacity: ${Specifications.OverallCapacity}`}
        />
      ) : (
        <Fragment />
      )}
      {Specifications.Receptacle ? (
        <SpecificationText text={`Receptacle: ${Specifications.Receptacle}`} />
      ) : (
        <Fragment />
      )}
      {Specifications.IsStarK ? (
        <SpecificationText text="Star K Certified" />
      ) : (
        <Fragment />
      )}

      {!isEmpty(Specifications.DischargeDimensions) ? (
        <div className="mt-4">
          <SpecificationText text="Discharge Dimension" />
          {Specifications.DischargeDimensions.map((specification) => {
            return (
              <SpecificationText
                key={specification.WebsiteDisplayName}
                text={`• ${specification.WebsiteDisplayName}`}
              />
            );
          })}
        </div>
      ) : (
        <Fragment />
      )}

      {!isEmpty(Specifications.FreezerSpecifications) ? (
        <div className="mt-4">
          <SpecificationText text="Freezer Specifications" />
          {Specifications.FreezerSpecifications.map((specification) => {
            return (
              <SpecificationText
                key={specification.WebsiteDisplayName}
                text={`• ${specification.WebsiteDisplayName}`}
              />
            );
          })}
        </div>
      ) : (
        <Fragment />
      )}

      {!isEmpty(Specifications.RefrigeratorSpecifications) ? (
        <div className="mt-4">
          <SpecificationText text="Refrigerator Specifications" />
          {Specifications.RefrigeratorSpecifications.map((specification) => {
            return (
              <SpecificationText
                key={specification.WebsiteDisplayName}
                text={`• ${specification.WebsiteDisplayName}`}
              />
            );
          })}
        </div>
      ) : (
        <Fragment />
      )}

      {!isEmpty(Specifications.GrillSpecifications) ? (
        <div className="mt-4">
          <SpecificationText text="Grill Specifications" />
          {Specifications.GrillSpecifications.map((specification) => {
            return (
              <SpecificationText
                key={specification.WebsiteDisplayName}
                text={`• ${specification.WebsiteDisplayName}`}
              />
            );
          })}
        </div>
      ) : (
        <Fragment />
      )}

      {!isEmpty(Specifications.RangetopSpecifications) ? (
        <div className="mt-4">
          <SpecificationText text="Rangetop Specifications" />
          {Specifications.RangetopSpecifications.map((specification) => {
            return (
              <SpecificationText
                key={specification.WebsiteDisplayName}
                text={`• ${specification.WebsiteDisplayName}`}
              />
            );
          })}
        </div>
      ) : (
        <Fragment />
      )}

      {!isEmpty(Specifications.BlowerOptions) ? (
        <div className="mt-4">
          <SpecificationText text="Blower Options" />
          {Specifications.BlowerOptions.map((specification) => {
            return (
              <SpecificationText
                key={specification.WebsiteDisplayName}
                text={`• ${specification.WebsiteDisplayName}`}
              />
            );
          })}
        </div>
      ) : (
        <Fragment />
      )}

      {!isEmpty(Specifications.CooktopSpecifications) ? (
        <div className="mt-4">
          <SpecificationText text="Cooktop Specifications" />
          {Specifications.CooktopSpecifications.map((specification) => {
            return (
              <SpecificationText
                key={specification.WebsiteDisplayName}
                text={`• ${specification.WebsiteDisplayName}`}
              />
            );
          })}
        </div>
      ) : (
        <Fragment />
      )}
    </div>
  );

  const renderModelOptions =
    attributes.ModelOptions &&
    attributes.ModelOptions.map((option) => {
      return (
        <SpecificationText key={option.Number} text={`${option.Number}`} />
      );
    });

  const renderAccessories =
    attributes.Accessories &&
    attributes.Accessories.map((option) => {
      return <SpecificationText key={option.Name} text={`${option.Name}`} />;
    });

  const renderProductOverview = (
    <div className="space-y-4 ">
      <ReactMarkdown
        className="text-subtitle leading-6 text-justify"
        rehypePlugins={[rehypeRaw]}
      >
        {attributes?.SupportingText1}
      </ReactMarkdown>
      <ReactMarkdown
        className="text-subtitle leading-6 text-justify"
        rehypePlugins={[rehypeRaw]}
      >
        {attributes?.SupportingText2}
      </ReactMarkdown>
    </div>
  );

  const productAccordionItems = [
    {
      label: 'Product Overview',
      details: renderProductOverview
    },
    { label: 'Specifications', details: renderSpecifications },
    { label: 'Model Options', details: renderModelOptions },
    { label: 'Accessories', details: renderAccessories }
  ];

  const groupAccordion = productAccordionItems.map((accordionItem) => {
    return (
      <Accordion
        key={accordionItem.label}
        expanded={expanded === accordionItem.label}
        onChange={handleChange(accordionItem.label)}
      >
        <AccordionSummary
          expandIcon={
            isEqual(expanded, accordionItem.label) ? (
              <FontAwesomeIcon
                className="text-black cursor-pointer"
                height={15}
                icon={faMinus}
                width={15}
              />
            ) : (
              <FontAwesomeIcon
                className="text-black cursor-pointer"
                height={15}
                icon={faPlus}
                width={15}
              />
            )
          }
        >
          <p className="w-full py-2 uppercase text-subtitle lg:text-body">
            {accordionItem.label}
          </p>
        </AccordionSummary>
        <AccordionDetails>{accordionItem.details}</AccordionDetails>
      </Accordion>
    );
  });

  return <S.Container>{groupAccordion}</S.Container>;
}

export default ProductAccordion;
